import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";

// ─── Project-scoped hooks ──────────────────────────────────────────────────

export const useMilestones = (projectId: string) => {
  return useQuery({
    queryKey: ["milestones", projectId],
    queryFn: () =>
      api.get(`/projects/${projectId}/milestones`, { params: { projectId } }),
    enabled: !!projectId,
  });
};

export const useCreateMilestone = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) =>
      api.post(`/projects/${projectId}/milestones`, data, {
        params: { projectId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
      queryClient.invalidateQueries({ queryKey: ["milestone-queue"] });
    },
  });
};

export const useSubmitMilestone = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      submission_notes,
      photos,
    }: {
      id: string;
      submission_notes: string;
      photos: File[];
    }) => {
      const formData = new FormData();
      formData.append("submission_notes", submission_notes);
      photos.forEach((photo) => formData.append("photos", photo));
      return api.post(`/milestones/${id}/submit`, formData, {
        params: { projectId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
      queryClient.invalidateQueries({ queryKey: ["milestone-queue"] });
    },
  });
};

export const useApproveMilestone = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.post(`/milestones/${id}/approve`, data, { params: { projectId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
      queryClient.invalidateQueries({ queryKey: ["milestone-queue"] });
      queryClient.invalidateQueries({ queryKey: ["milestone-pending-count"] });
    },
  });
};

export const useRejectMilestone = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { notes: string } }) =>
      api.post(`/milestones/${id}/reject`, data, { params: { projectId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
      queryClient.invalidateQueries({ queryKey: ["milestone-queue"] });
      queryClient.invalidateQueries({ queryKey: ["milestone-pending-count"] });
    },
  });
};

export const useMilestonePhotos = (projectId: string, id: string) => {
  return useQuery({
    queryKey: ["milestones", projectId, id, "photos"],
    queryFn: () => api.get(`/projects/${projectId}/milestones/${id}/photos`),
    enabled: !!projectId && !!id,
  });
};

export const useUpdateMilestone = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.patch(`/projects/${projectId}/milestones/${id}`, data, {
        params: { projectId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
    },
  });
};

export const useDeleteMilestone = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete(`/projects/${projectId}/milestones/${id}`, {
        params: { projectId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", projectId] });
    },
  });
};

// ─── Global / standalone hooks ─────────────────────────────────────────────

export const useMilestoneQueue = (params?: {
  tab?: string;
  projectId?: string;
  sort?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ["milestone-queue", params],
    queryFn: () => api.get("/milestones/queue", { params }),
  });
};

export const usePendingMilestoneCount = () => {
  return useQuery({
    queryKey: ["milestone-pending-count"],
    queryFn: () => api.get("/milestones/pending-count"),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
};

export const useMilestone = (id: string, projectId?: string) => {
  return useQuery({
    queryKey: ["milestone", id],
    queryFn: () => api.get(`/milestones/${id}`, { params: { projectId } }),
    enabled: !!id,
  });
};
